import React, { useState, useEffect } from 'react';
import {
    Box,
    Container,
    Heading,
    Text,
    SimpleGrid,
    VStack,
    HStack,
    Icon,
    Button,
    Spinner,
    useToast,
    Badge,
    Flex,
    Divider,
    Avatar
} from '@chakra-ui/react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FaArrowRight, FaCheckCircle, FaBookOpen, FaGraduationCap, FaArrowLeft, FaLayerGroup } from 'react-icons/fa';
import * as FaIcons from 'react-icons/fa';
import niveauService from '../services/niveauService';
import RegistrationModal from '../shared/RegistrationModal';
import Header from '../layout/Header';
import Footer from '../layout/Footer';

const MotionBox = motion(Box);

const NiveauDetails = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [niveau, setNiveau] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const toast = useToast();

    const primaryColor = '#F2C919';
    const secondaryColor = '#2C3E50';

    useEffect(() => {
        fetchNiveauDetails();
    }, [id]);

    const fetchNiveauDetails = async () => {
        try {
            const response = await niveauService.getNiveaux();
            const foundNiveau = response.data.find(n => n._id === id);
            if (foundNiveau) {
                setNiveau(foundNiveau);
            } else {
                toast({
                    title: 'المستوى غير موجود',
                    description: 'يرجى اختيار مستوى دراسي صالح.',
                    status: 'warning',
                    duration: 3000,
                    isClosable: true,
                    position: 'top'
                });
                navigate('/discover');
            }
        } catch (error) {
            toast({
                title: 'خطأ في تحميل التفاصيل',
                description: 'حدث خطأ أثناء جلب بيانات المستوى.',
                status: 'error',
                duration: 4000,
                isClosable: true,
                position: 'top'
            });
        } finally {
            setLoading(false);
        }
    };

    const getIconComponent = (iconName) => {
        return FaIcons[iconName] || FaGraduationCap;
    };

    if (loading) {
        return (
            <Box minH="100vh" display="flex" alignItems="center" justifyContent="center" bg="gray.50">
                <Spinner size="xl" color={primaryColor} thickness="4px" />
            </Box>
        );
    }

    if (!niveau) return null;

    const IconComponent = getIconComponent(niveau.icon);

    return (
        <Box dir="rtl" bg="gray.50" minH="100vh">
            <Header />

            {/* Hero Section - Matching PacksPage/CourseDiscovery Style */}
            <Box
                bgGradient={`linear(to-l, ${secondaryColor}, gray.800)`}
                py={{ base: 20, md: 32 }}
                color="white"
                position="relative"
                overflow="hidden"
            >
                <Box
                    position="absolute"
                    top="-10%"
                    right="-10%"
                    width="500px"
                    height="500px"
                    bg={primaryColor}
                    opacity="0.05"
                    borderRadius="full"
                    filter="blur(120px)"
                />

                <Container maxW="1400px" position="relative" zIndex={1}>
                    <Button
                        leftIcon={<Icon as={FaArrowRight} ml={1} />}
                        onClick={() => navigate('/discover')}
                        variant="ghost"
                        color="whiteAlpha.800"
                        _hover={{ color: primaryColor, bg: 'whiteAlpha.100' }}
                        fontFamily="Cairo, sans-serif"
                        fontWeight="700"
                        mb={10}
                        size="md"
                    >
                        العودة لقائمة المستويات
                    </Button>

                    <Flex
                        direction={{ base: 'column', lg: 'row' }}
                        align="center"
                        justify="space-between"
                        gap={16}
                    >
                        {/* Info Block */}
                        <VStack align={{ base: 'center', lg: 'start' }} spacing={8} flex={1}>
                            <Badge
                                bg={primaryColor}
                                color={secondaryColor}
                                px={6}
                                py={2}
                                borderRadius="full"
                                fontSize="md"
                                fontFamily="Cairo, sans-serif"
                                fontWeight="800"
                            >
                                عرض تفاصيل المستوى
                            </Badge>

                            <VStack align={{ base: 'center', lg: 'start' }} spacing={4}>
                                <Heading fontSize={{ base: '4xl', md: '6xl' }} fontFamily="Cairo, sans-serif" fontWeight="900" lineHeight="1.2">
                                    {niveau.name}
                                </Heading>
                                <HStack spacing={4}>
                                    <Avatar bg={`${primaryColor}20`} icon={<Icon as={IconComponent} color={primaryColor} />} />
                                    <Text fontSize="xl" fontFamily="Tajawal, sans-serif" color="whiteAlpha.900" fontWeight="700">
                                        {niveau.matieres?.length || 0} مادة تعليمية متوفرة
                                    </Text>
                                </HStack>
                            </VStack>

                            <Text
                                fontSize="lg"
                                fontFamily="Tajawal, sans-serif"
                                color="whiteAlpha.800"
                                lineHeight="2"
                                maxW="700px"
                                textAlign={{ base: 'center', lg: 'start' }}
                            >
                                استكشف قائمة المواد المتاحة لهذا المستوى الدراسي. نحن نضمن لك محتوى تعليمياً عالي الجودة متوافقاً مع البرامج الرسمية لوزارة التربية، لمساعدتك على التفوق والنجاح.
                            </Text>
                        </VStack>

                        {/* Registration Widget */}
                        <MotionBox
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.5 }}
                        >
                            <Box
                                bg="white"
                                p={{ base: 8, md: 12 }}
                                borderRadius="3xl"
                                boxShadow="2xl"
                                border="2px solid"
                                borderColor="gray.100"
                                maxW="450px"
                                textAlign="center"
                            >
                                <VStack spacing={6}>
                                    <Box p={5} bg={`${primaryColor}15`} borderRadius="2xl" color={primaryColor}>
                                        <Icon as={FaCheckCircle} boxSize={12} />
                                    </Box>
                                    <VStack spacing={2}>
                                        <Heading size="lg" fontFamily="Cairo, sans-serif" fontWeight="900" color={secondaryColor}>
                                            ابدأ الآن
                                        </Heading>
                                        <Text fontFamily="Tajawal, sans-serif" color="gray.500" fontWeight="600">
                                            كن جزءاً من منصة كوبرا واحصل على مرافقة بيداغوجية شاملة.
                                        </Text>
                                    </VStack>
                                    <Button
                                        w="100%"
                                        h="65px"
                                        bg={primaryColor}
                                        color={secondaryColor}
                                        fontSize="xl"
                                        fontWeight="900"
                                        fontFamily="Cairo, sans-serif"
                                        borderRadius="xl"
                                        boxShadow="lg"
                                        onClick={() => setIsModalOpen(true)}
                                        _hover={{ bg: '#d4b016', transform: 'translateY(-3px)' }}
                                        _active={{ transform: 'translateY(0)' }}
                                        rightIcon={<Icon as={FaGraduationCap} mr={2} />}
                                    >
                                        انضم للمستوى
                                    </Button>
                                </VStack>
                            </Box>
                        </MotionBox>
                    </Flex>
                </Container>
            </Box>

            {/* Content List */}
            <Container maxW="1400px" py={20}>
                <VStack spacing={4} mb={16} textAlign="center">
                    <Heading size="2xl" color={secondaryColor} fontFamily="Cairo, sans-serif" fontWeight="900">
                        المواد المتاحة للتلاميذ
                    </Heading>
                    <Text fontSize="lg" color="gray.500" maxW="2xl" fontFamily="Tajawal, sans-serif" fontWeight="600">
                        مجموعة مختارة من المواد الدراسية مع شرح مفصل وتمارين تطبيقية.
                    </Text>
                </VStack>

                <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={8}>
                    {niveau.matieres?.map((matiere, index) => {
                        const MatiereIcon = getIconComponent(matiere.icon);
                        return (
                            <MotionBox
                                key={index}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.4, delay: index * 0.05 }}
                                whileHover={{ y: -8 }}
                            >
                                <Box
                                    bg="white"
                                    p={8}
                                    borderRadius="2xl"
                                    boxShadow="lg"
                                    border="2px solid"
                                    borderColor="gray.100"
                                    transition="all 0.3s"
                                    _hover={{ borderColor: primaryColor, boxShadow: 'xl' }}
                                    h="100%"
                                >
                                    <HStack spacing={5} align="flex-start">
                                        <Box p={4} bg={`${niveau.color || primaryColor}15`} borderRadius="xl" color={niveau.color || secondaryColor}>
                                            <Icon as={MatiereIcon} boxSize={8} />
                                        </Box>
                                        <VStack align="flex-start" spacing={2}>
                                            <Heading size="md" fontFamily="Cairo, sans-serif" fontWeight="900" color={secondaryColor}>
                                                {matiere.name}
                                            </Heading>
                                            {matiere.prix > 0 && (
                                                <Badge
                                                    bg={primaryColor}
                                                    color={secondaryColor}
                                                    px={3}
                                                    py={1}
                                                    borderRadius="full"
                                                    fontSize="sm"
                                                    fontWeight="800"
                                                    fontFamily="Cairo, sans-serif"
                                                >
                                                    {matiere.prix} DT / شهر
                                                </Badge>
                                            )}
                                            <Text fontSize="sm" fontFamily="Tajawal, sans-serif" color="gray.500" fontWeight="600" lineHeight="1.6">
                                                شروحات مبسطة، تمارين مصححة، وحصص مباشرة لتغطية كافة جوانب المادة.
                                            </Text>
                                        </VStack>
                                    </HStack>
                                </Box>
                            </MotionBox>
                        );
                    })}
                </SimpleGrid>

                {(!niveau.matieres || niveau.matieres.length === 0) && (
                    <Box textAlign="center" py={20}>
                        <Icon as={FaBookOpen} boxSize={16} color="gray.200" mb={4} />
                        <Heading size="md" color="gray.400" fontFamily="Cairo, sans-serif">لا توجد مواد مضافة حالياً</Heading>
                    </Box>
                )}

                {/* Final CTA Strip */}
                <Box
                    mt={32}
                    bgGradient={`linear(to-l, ${primaryColor}, #E0B817)`}
                    borderRadius="3xl"
                    p={{ base: 10, md: 16 }}
                    textAlign="center"
                    boxShadow="2xl"
                >
                    <VStack spacing={8}>
                        <Heading size="xl" color={secondaryColor} fontFamily="Cairo, sans-serif" fontWeight="900">
                            هل أنت مستعد للتفوق في {niveau.name}؟
                        </Heading>
                        <Text fontSize="xl" color={secondaryColor} maxW="3xl" fontFamily="Tajawal, sans-serif" fontWeight="700">
                            سجل الآن وابدأ في تحسين مستواك الدراسي مع أفضل الأدوات التعليمية في تونس.
                        </Text>
                        <Button
                            size="xl"
                            h="70px"
                            px={12}
                            bg={secondaryColor}
                            color="white"
                            _hover={{ bg: 'black', transform: 'scale(1.05)' }}
                            fontFamily="Cairo, sans-serif"
                            fontWeight="900"
                            onClick={() => setIsModalOpen(true)}
                            borderRadius="2xl"
                            boxShadow="xl"
                        >
                            سجل في هذا المستوى الآن
                        </Button>
                    </VStack>
                </Box>
            </Container>

            <RegistrationModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                niveau={niveau}
            />

            <Footer />
        </Box>
    );
};

export default NiveauDetails;
